import { LayerScopeConfig, ScopeType } from "src/js/stores/site/store";
import { measurementString, measurementValue, unitString } from "react-migration/lib/util/conversion";
import { Checkbox } from "react-migration/components/Checkbox";
import { Comparable } from "react-migration/domains/comparables/hooks/useComparablePointSearch";
import Feature from "src/js/stores/user/Feature";
import { MeasurementSystem } from "src/js/stores/user/store";
import { Table } from "react-migration/components/TableDeprecated";
import clsx from "clsx";
import { convertLengthToAllUnits } from "react-migration/lib/util/measurements";
import { getMeasurementSystemPreference } from "src/js/stores/user/actions/getSetting";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import meanBy from "lodash/meanBy";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";

type ComparablesPrintableTableProps = {
  comparables: Comparable[];
  hiddenIds: string[];
  allVisible: boolean;
  scopeConfig: LayerScopeConfig;
  scopeType: ScopeType;
  toggleId: (id: string) => void;
  toggleAll: () => void;
  comparableIdentifier: (comparable: Comparable) => string;
};

type PricedAndEstimatedAndFloorAreaComp = Omit<Comparable, "total_floor_area" | "price" | "est_price"> & {
  total_floor_area: number;
  price: number;
  est_price: number;
};

const kFormat = (value: number) => {
  return Math.abs(value) > 999
    ? ((Math.sign(value) * Math.abs(value)) / 1000).toFixed(1) + "k"
    : (Math.sign(value) * Math.abs(value)).toFixed(1);
};

export const ComparablesPrintableTable = ({
  comparables,
  hiddenIds,
  allVisible,
  toggleId,
  toggleAll,
  scopeConfig,
  scopeType = ScopeType.ON_SITE,
  comparableIdentifier,
}: ComparablesPrintableTableProps) => {
  const { t } = useTranslation();
  const isImperial = getMeasurementSystemPreference() === MeasurementSystem.IMPERIAL;
  const isUSUser = hasFeature(Feature.usAccess);
  const measurementUnit = isImperial ? "feet" : "meters";

  const avgSoldPrice = meanBy(
    comparables?.filter(
      (comparable) => comparable.price && comparable.total_floor_area
    ) as PricedAndEstimatedAndFloorAreaComp[],
    (comparable) => comparable.price / measurementValue(comparable.total_floor_area, "m2", isImperial)
  );
  const avgMarketValue = meanBy(
    comparables?.filter(
      (comparable) => comparable.est_price && comparable.total_floor_area
    ) as PricedAndEstimatedAndFloorAreaComp[],
    (comparable) => comparable.est_price / measurementValue(comparable.total_floor_area, "m2", isImperial)
  );

  if (!comparables?.length) return null;

  return (
    <>
      <h3 className="text-sm">
        {scopeType === ScopeType.ON_SITE
          ? t("site_report.contextual_data.on_site")
          : t("site_report.contextual_data.nearby", {
              radius: Math.round(convertLengthToAllUnits(scopeConfig.radius ?? 100, "meters")[measurementUnit]),
              unit: measurementUnit,
            })}
      </h3>
      <div className="mb-4">
        <div>
          {t("sites.card.site_report.comparables.avg_sold_price")} ({isUSUser ? "$" : "£"}/
          {unitString("m2", isImperial)}): {kFormat(avgSoldPrice)}
        </div>
        <div>
          {t("sites.card.site_report.comparables.avg_market_value")} ({isUSUser ? "$" : "£"}/
          {unitString("m2", isImperial)}): {kFormat(avgMarketValue)}
        </div>
      </div>

      <Table<Comparable, "address">
        size="small"
        primaryKey="address"
        rowClassName={(comparable) =>
          clsx("print:break-inside-avoid", {
            "bg-red-50! print:hidden": !!comparable && hiddenIds.includes(comparableIdentifier(comparable)),
          })
        }
        headerCellClassName={(column) =>
          clsx("print:w-[100px] print:wrap-normal", {
            "print:hidden": column.id === "checkbox",
            "print:w-[500px]": column.id === "address",
          })
        }
        cellClassName={(_, column) =>
          clsx({
            "print:hidden": column.id === "checkbox",
          })
        }
        columns={[
          {
            id: "checkbox",
            label: (
              <div>
                <Checkbox checked={allVisible} onCheckedChange={toggleAll} />
              </div>
            ),
            render: (comparable: Comparable) => (
              <div>
                <Checkbox
                  checked={!hiddenIds.includes(comparableIdentifier(comparable))}
                  onCheckedChange={() => toggleId(comparableIdentifier(comparable))}
                />
              </div>
            ),
          },
          {
            label: t("sites.card.site_report.comparables.address"),
            render: ({ address }) => (
              <span className="print:whitespace-pre-line whitespace-break-spaces">{address}</span>
            ),
            grow: true,
          },
          {
            label: t("sites.card.site_report.comparables.date_sold"),
            render: (row) => new Date(row.date_of_transfer).toLocaleDateString(),
            grow: true,
          },
          {
            label: t("sites.card.site_report.comparables.sold_price", {
              price: isUSUser ? "$" : "£",
            }),
            render: ({ price }) => kFormat(price),
            grow: true,
          },
          {
            label: t("sites.card.site_report.comparables.est_market_value", {
              currency: isUSUser ? "$" : "£",
            }),
            render: ({ est_price }) => (est_price ? kFormat(est_price) : undefined),
            grow: true,
          },
          {
            label: t("sites.card.site_report.comparables.new_build"),
            render: ({ is_new_property }) => (is_new_property ? "Yes" : "No"),
            grow: true,
          },
          {
            label: t("sites.card.site_report.comparables.property_type"),
            rowKey: "category",
            grow: true,
          },
          {
            label: t("sites.card.site_report.comparables.est_bedrooms"),
            render: ({ est_bedrooms }) => est_bedrooms?.join("-"),
            grow: true,
          },
          {
            label: t("sites.card.site_report.comparables.floor_area", {
              area: unitString("m2", isImperial),
            }),
            render: ({ total_floor_area }) =>
              total_floor_area ? measurementString(total_floor_area, "m2", isImperial, false) : undefined,
            grow: true,
          },
          {
            label: t("sites.card.site_report.comparables.sold_price", {
              price: `${isUSUser ? "$" : "£"}/${unitString("m2", isImperial)}`,
            }),
            render: ({ price, total_floor_area }) =>
              price && total_floor_area
                ? kFormat(price / measurementValue(total_floor_area, "m2", isImperial))
                : undefined,
            grow: true,
          },
          {
            label: t("sites.card.site_report.comparables.market_value", {
              value: `${isUSUser ? "$" : "£"}/${unitString("m2", isImperial)}`,
            }),
            render: ({ est_price, total_floor_area }) =>
              est_price && total_floor_area
                ? kFormat(est_price / measurementValue(total_floor_area, "m2", isImperial))
                : undefined,
            grow: true,
          },
          { label: t("sites.card.site_report.comparables.tenure"), rowKey: "tenure", grow: true },
        ]}
        rows={comparables}
        tableClassName="block overflow-x-scroll print:overflow-hidden print-landscape"
      />
    </>
  );
};
